/**
 * Trend direction arrow, reskinned to the Zeplin SPOT dashboard design.
 *
 * Uses the flat 16x16 rounded "arrow badge" icons from the design. Each
 * direction has its own baked-in color, so this component renders the matching
 * SVG directly:
 *   UP    -> green  (#4ADE80)
 *   DOWN  -> red    (#F87171)
 *   FLAT  -> blue   (#CEDDFC)  (RIGHT / NONE)
 */
function ArrowUp({ size, clipId }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath={`url(#${clipId})`} fill="var(--font-strong-success)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1.324 3.785c0-.736.596-1.333 1.333-1.333h9.428a1.333 1.333 0 1 1 0 2.666H2.657a1.333 1.333 0 0 1-1.333-1.333z"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.085 14.547c.736 0 1.333-.597 1.333-1.334V3.785a1.333 1.333 0 0 0-2.666 0v9.428c0 .737.597 1.334 1.333 1.334z"
        />
        <path d="M2.657 13.213a1.333 1.333 0 0 1 0-1.885l6.6-6.6a1.333 1.333 0 1 1 1.885 1.886l-6.6 6.6c-.52.52-1.364.52-1.885 0z" />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="16" height="16" rx="8" fill="#fff" />
        </clipPath>
      </defs>
    </svg>
  );
}

function ArrowDown({ size, clipId }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath={`url(#${clipId})`} fill="var(--font-strong-strong-error)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.085 1.509c.736 0 1.333.597 1.333 1.333v9.428a1.333 1.333 0 0 1-2.666 0V2.842c0-.736.597-1.333 1.333-1.333z"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1.324 12.27c0 .737.596 1.334 1.333 1.334h9.428a1.333 1.333 0 0 0 0-2.667H2.657c-.737 0-1.333.597-1.333 1.333z"
        />
        <path d="M2.657 2.842c.52-.52 1.365-.52 1.885 0l6.6 6.6a1.333 1.333 0 0 1-1.885 1.886l-6.6-6.6a1.333 1.333 0 0 1 0-1.886z" />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="16" height="16" rx="8" fill="#fff" />
        </clipPath>
      </defs>
    </svg>
  );
}

function ArrowStraight({ size, clipId }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath={`url(#${clipId})`} fill="var(--font-heavy-default)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6.233.418c.52-.52 1.365-.52 1.886 0l6.666 6.667A1.333 1.333 0 0 1 12.9 8.97L6.233 2.304a1.333 1.333 0 0 1 0-1.886z"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6.233 15.637c.52.52 1.365.52 1.886 0l6.666-6.666A1.333 1.333 0 0 0 12.9 7.085l-6.667 6.667c-.52.52-.52 1.364 0 1.885z"
        />
        <path d="M.509 8.028c0-.737.597-1.334 1.333-1.334h9.334a1.333 1.333 0 1 1 0 2.667H1.842A1.333 1.333 0 0 1 .51 8.028z" />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="16" height="16" rx="8" fill="#fff" />
        </clipPath>
      </defs>
    </svg>
  );
}

const ARROW_BY_DIRECTION = {
  UP: ArrowUp,
  DOWN: ArrowDown,
  RIGHT: ArrowStraight,
  NONE: ArrowStraight
};

export default function TrendArrow({
  direction = "RIGHT",
  size = 16,
  "data-testid": dataTestId
}) {
  const dir = ARROW_BY_DIRECTION[direction] ? direction : "RIGHT";
  const Arrow = ARROW_BY_DIRECTION[dir];
  // Unique clipPath id per direction so multiple arrows on the page don't
  // collide on the same SVG def.
  const clipId = `trend-arrow-clip-${dir.toLowerCase()}`;

  return (
    <span
      style={{ display: "inline-flex", flexShrink: 0 }}
      data-testid={dataTestId}
      aria-hidden="true"
    >
      <Arrow size={size} clipId={clipId} />
    </span>
  );
}
